import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Building, MapPin, User, UserCog, Briefcase, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export const Footer = () => {
  const { t } = useLanguage();

  const handleAppStoreClick = () => {
    window.location.href = '/download';
  };

  const handlePlayStoreClick = () => {
    window.location.href = '/download';
  };

  return (
    <footer className="border-t border-border bg-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png" 
                alt="Youse Capital" 
                className="h-[144px] w-auto"
              />
            </div>
            <div className="disclaimer" data-testid="disclaimer">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <p>{t('disclaimer')}</p>
              </div>
            </div>
            
            {/* App Store Buttons */}
            <div className="mt-6">
              <p className="text-sm font-medium text-muted-foreground mb-3">Download our app</p>
              <div className="flex gap-3">
                <button
                  onClick={handleAppStoreClick}
                  className="transition-transform hover:scale-105 active:scale-95"
                  data-testid="app-store-btn"
                >
                  <svg width="135" height="40" viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg">
                    <rect width="135" height="40" rx="5" fill="#000"/>
                    <path d="M30.128 19.784c-.029-3.223 2.639-4.791 2.761-4.864-1.511-2.203-3.853-2.504-4.676-2.528-1.967-.207-3.875 1.177-4.877 1.177-1.016 0-2.553-1.157-4.207-1.124-2.131.033-4.122 1.265-5.215 3.183-2.26 3.903-.576 9.64 1.588 12.799 1.085 1.553 2.355 3.287 4.008 3.226 1.62-.066 2.228-1.034 4.186-1.034 1.943 0 2.518 1.034 4.2.995 1.739-.028 2.838-1.56 3.888-3.127 1.259-1.778 1.767-3.529 1.79-3.618-.04-.015-3.412-1.301-3.446-5.185z" fill="#fff"/>
                    <path d="M26.928 10.306c.874-1.093 1.472-2.58 1.306-4.089-1.265.056-2.847.875-3.758 1.944-.806.942-1.526 2.486-1.34 3.938 1.421.106 2.88-.717 3.792-1.793z" fill="#fff"/>
                    <text x="50" y="15" fill="#fff" fontSize="8" fontFamily="Arial">Download on the</text>
                    <text x="50" y="28" fill="#fff" fontSize="14" fontFamily="Arial" fontWeight="bold">App Store</text>
                  </svg>
                </button>
                <button
                  onClick={handlePlayStoreClick}
                  className="transition-transform hover:scale-105 active:scale-95"
                  data-testid="play-store-btn"
                >
                  <svg width="135" height="40" viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg">
                    <rect width="135" height="40" rx="5" fill="#000"/>
                    <path d="M15.769 8.386L24.493 13.38 17.29 20l-7.203-6.62 5.682-4.994z" fill="#36A852"/>
                    <path d="M10.087 13.38v13.24l7.203-6.62-7.203-6.62z" fill="#FBBC04"/>
                    <path d="M15.769 31.614L10.087 26.62l7.203-6.62 7.203 6.62-8.724 4.994z" fill="#EA4335"/>
                    <path d="M24.493 13.38l-7.203 6.62 7.203 6.62 3.085-1.765c1.105-.632 1.105-2.21 0-2.842L24.493 13.38z" fill="#4285F4"/>
                    <text x="38" y="15" fill="#fff" fontSize="8" fontFamily="Arial">GET IT ON</text>
                    <text x="38" y="28" fill="#fff" fontSize="13" fontFamily="Arial" fontWeight="bold">Google Play</text>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Products</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/signup" className="hover:text-foreground transition-colors">Personal Accounts</Link></li>
              <li><Link to="/signup" className="hover:text-foreground transition-colors">Business Accounts</Link></li>
              <li><Link to="/transfer-info" className="hover:text-foreground transition-colors">International Transfers</Link></li>
              <li><Link to="/deposit" className="hover:text-foreground transition-colors">Crypto Deposits</Link></li>
              <li><Link to="/co-application" className="hover:text-foreground transition-colors font-medium text-primary">Commercial Officer Program</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground transition-colors">About Us</Link></li>
              <li><Link to="/careers" className="hover:text-foreground transition-colors">Careers</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/transfer-info" className="hover:text-foreground transition-colors">Transfer Information</Link></li>
              <li><Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>

        {/* Login Options */}
        <div className="border-t border-border mt-8 pt-6">
          <h4 className="font-semibold mb-4 text-center">Portal Access</h4>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/login" 
              className="flex items-center gap-2 px-4 py-2 bg-[#1a365d] text-white rounded-lg hover:bg-[#2d4a7c] transition-colors"
              data-testid="footer-login-user"
            >
              <User className="w-4 h-4" />
              <span>Login User</span>
            </Link>
            <Link 
              to="/login?role=co" 
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="footer-login-co"
            >
              <Briefcase className="w-4 h-4" />
              <span>Login Commercial Officer</span>
            </Link>
            <Link 
              to="/login?role=admin" 
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors"
              data-testid="footer-login-admin"
            >
              <UserCog className="w-4 h-4" />
              <span>Login Admin</span>
            </Link>
          </div>
        </div>

        {/* Company Registration Info */}
        <div className="border-t border-border mt-8 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <Building className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="font-medium text-foreground">Company Number</p>
                <p>7903132</p>
                <p>Delaware (US)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="font-medium text-foreground">Registered Address</p>
                <p>Sussex, DE</p>
                <p>United States</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 mt-0.5 text-primary" />
              <div>
                <p className="font-medium text-foreground">Agent Address</p>
                <p>16192 COASTAL HWY</p>
                <p>LEWES, DE 19958</p>
              </div>
            </div>
          </div>
        </div>

        {/* SEC Regulatory Information */}
        <div className="border-t border-border mt-6 pt-6">
          <div className="flex flex-col items-center gap-4">
            <img 
              src="/assets/sec-logo.png" 
              alt="U.S. Securities and Exchange Commission" 
              className="h-16 w-auto opacity-80 hover:opacity-100 transition-opacity"
            />
            <div className="flex flex-col sm:flex-row items-center gap-3 text-sm">
              <a 
                href="https://www.sec.gov/divisions/corpfin/cf-noaction/sparta100406.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                data-testid="sec-regulation-link"
              >
                <Shield className="w-4 h-4" />
                <span>Regulated by SEC</span>
                <ExternalLink className="w-3 h-3" />
              </a>
              <span className="hidden sm:inline text-muted-foreground">|</span>
              <a 
                href="/assets/sparta-sec-filing.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                data-testid="sec-filing-pdf-link"
              >
                <FileText className="w-4 h-4" />
                <span>SEC Filing Document</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-border mt-6 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © 2021 Youse Capital LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground">Member of U.S. Securities and Exchange Commission</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
